import React, { useState } from 'react';

import { ExclamationCircleOutlined } from '@ant-design/icons';
import { message, Modal, Switch } from 'antd';
import { T } from 'react-translator-component';
import styled from 'styled-components';


import {
  deleteConnection,
  reactivateContract,
  revokeContract,
  setReceivedVisibility,
  suspendContract,
} from '../../../hooks/interoperability';
import { Connection } from '../../../types/responses/Interoperability';
import StatusBadge from '../../atoms/StatusBadge';
import {
  STATUS_STYLE,
  canToggleReceiving,
  directionHint,
  directionLabel,
  isEffective,
  receivingLabel,
  sharingLabel,
  suspendWarning,
} from '../../organisms/Interoperability/contractRules';
import { ScopeAccountsRow } from '../../organisms/Interoperability/InvitesTab/ScopeAccountsRow';


/**
 * Detalhe da conexao, no painel lateral (MV2-1713).
 *
 * Mesmo formato dos detalhes do financeiro: cartoes com titulo e linhas de
 * rotulo/valor. O que era coluna apertada na tabela antiga cabe aqui com nome
 * inteiro — em especial o escopo, que e uma lista de contas e nunca caberia numa
 * celula.
 *
 * As acoes que mudam contrato ficam so aqui, e nao na linha da tabela: suspender e
 * revogar mexem no que o app do parceiro exibe, e nao sao coisas para clicar de
 * passagem numa lista.
 */

const Card = styled.div`
  display: flex;
  flex-direction: column;
  border: 1px solid #d1d5db;
  border-radius: 12px;
  padding: 12px 16px;
  margin-bottom: 24px;
`;

const CardTitle = styled.h4`
  border-bottom: 1px solid #d1d5db;
  margin: 0 -16px;
  padding: 0 16px 8px;
`;

const Row = styled.p`
  display: flex;
  align-items: center;
  justify-content: space-between;
  color: #8d929e;
  font-weight: 400;
  font-size: 12px;
  border-bottom: 1px solid #f3f4f6;
  padding: 8px 0;
  margin-bottom: 0;

  &:last-of-type {
    border-bottom: none;
  }
`;

const Value = styled.span`
  color: #000;
  font-size: 12px;
  text-align: right;
  margin-left: 8px;
`;

const StrongValue = styled(Value)`
  font-weight: 600;
`;

const Hint = styled.p`
  color: #8a9099;
  font-size: 11px;
  line-height: 1.5;
  margin: 8px 0 0;
`;

const Actions = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`;

const ActionButton = styled.button<{ danger?: boolean }>`
  background: #fff;
  color: ${({ danger }) => (danger ? '#b02525' : '#4a5462')};
  border: 1px solid ${({ danger }) => (danger ? '#f3c7c7' : '#d9dce1')};
  border-radius: 6px;
  padding: 8px 14px;
  cursor: pointer;
  font-size: 12px;

  &:disabled {
    color: #a9b4c2;
    cursor: not-allowed;
  }
`;

export type ConnectionDrawerDetailsProps = {
  tenantId: string;
  connection: Connection;

  /** Recarrega a lista depois de suspender, reativar ou revogar. */
  onChanged: () => void;

  /** Fecha o painel: revogar encerra o contrato, e o detalhe deixa de existir. */
  onClose: () => void;
};

export const ConnectionDrawerDetails: React.FC<ConnectionDrawerDetailsProps> = ({
  tenantId,
  connection,
  onChanged,
  onClose,
}) => {
  const [busy, setBusy] = useState(false);

  const status = STATUS_STYLE[connection.status];
  const toggleEnabled = canToggleReceiving(connection);

  /**
   * Executa a acao e DIZ o resultado.
   *
   * Antes havia try/finally sem catch: uma falha de rede ao revogar apenas
   * devolvia o botao ao estado normal, e a pessoa saia acreditando que o contrato
   * tinha sido encerrado. Em acao destrutiva, silencio e pior que erro.
   */
  const run = async (action: () => Promise<void>, success: string, closeAfter = false) => {
    try {
      setBusy(true);
      await action();
      onChanged();
      message.success(success);

      if (closeAfter) {
        onClose();
      }
    } catch {
      message.error(T('Could not complete the action. Nothing was changed.'));
    } finally {
      setBusy(false);
    }
  };

  /**
   * Suspender pede confirmacao (MV2-1809).
   *
   * E reversivel, mas tira estacoes do ar no app na hora, e o botao ficava a um
   * clique de distancia sem nenhum passo entre. A confirmacao diz quantas estacoes
   * somem e de qual app — nao "tem certeza?" —, porque o numero e o que deixa
   * obvio se o clique foi na linha certa.
   *
   * Sem `danger`: o vermelho e de revogar e excluir, que nao voltam atras.
   * Reativar continua direto: pedir confirmacao para restaurar so atrapalha.
   */
  const suspend = () => {
    Modal.confirm({
      title: T('Suspend this contract?'),
      icon: <ExclamationCircleOutlined />,
      content: suspendWarning(connection),
      okText: T('Suspend'),
      cancelText: T('Cancel'),
      onOk: () =>
        run(
          () => suspendContract(tenantId, connection.partner_tenant_id),
          T('Contract suspended.'),
        ),
    });
  };

  /**
   * Revogar encerra o contrato e nao e reversivel, e a confirmacao diz isso em vez
   * de perguntar apenas "tem certeza?".
   *
   * Modal.confirm do antd, como o resto do dashboard.
   */
  const revoke = () => {
    Modal.confirm({
      title: T('Revoke the contract with this partner?'),
      icon: <ExclamationCircleOutlined />,
      content: T(
        'Revoking is not reversible: the stations stop being displayed on both sides, and reconnecting requires a new invite.',
      ),
      okText: T('Revoke'),
      cancelText: T('Cancel'),
      okButtonProps: { danger: true },
      onOk: () =>
        run(
          () => revokeContract(tenantId, connection.partner_tenant_id),
          T('Contract revoked.'),
          true,
        ),
    });
  };

  /**
   * Excluir apaga de vez a conexao (o contrato inteiro). So aparece quando ja esta revogada, e
   * a confirmacao deixa claro que e permanente — o backend recusa se algo ainda nao estiver
   * revogado.
   */
  const remove = () => {
    Modal.confirm({
      title: T('Delete this revoked connection?'),
      icon: <ExclamationCircleOutlined />,
      content: T(
        'Deleting removes the contract for good. This cannot be undone; the audit trail keeps the record.',
      ),
      okText: T('Delete'),
      cancelText: T('Cancel'),
      okButtonProps: { danger: true },
      onOk: () =>
        run(
          () => deleteConnection(tenantId, connection.partner_tenant_id),
          T('Connection deleted.'),
          true,
        ),
    });
  };

  return (
    <div>
      <Card>
        <CardTitle>{T('Contract')}</CardTitle>

        <Row style={{ marginTop: 8 }}>
          {T('Type')}
          <Value>{directionLabel(connection.contract_type)}</Value>
        </Row>

        {/*
          A seta sozinha nao diz de quem e a estacao. Aqui, que e a tela de detalhe, cabe a frase
          inteira — os dois unilaterais sao opostos e a diferenca importa antes de revogar.
        */}
        <Hint>{directionHint(connection.contract_type)}</Hint>

        <Row>
          {T('Status')}
          <Value>
            <StatusBadge tone={{ bg: status.bg, fg: status.fg, dot: status.dot }}>
              {T(status.label)}
            </StatusBadge>
          </Value>
        </Row>

        <Row>
          {T('Partner app')}
          <Value>
            {connection.partner_has_app ? T('app configured') : T('no app')}
          </Value>
        </Row>

        {(connection.partner_account_count || 0) > 1 && (
          <Row>
            {T('Partner accounts in this contract')}
            <StrongValue>{connection.partner_account_count}</StrongValue>
          </Row>
        )}

        <Row>
          {T('Last session')}
          <Value>
            {connection.last_session_at?.slice(0, 16).replace('T', ' ') || '—'}
          </Value>
        </Row>

        {!connection.partner_has_app && (
          <Hint>
            {T(
              'Without an app the partner can share stations but receives none, so the contract cannot be bidirectional.',
            )}
          </Hint>
        )}
      </Card>

      <Card>
        <CardTitle>{T('Stations I share')}</CardTitle>

        <Row style={{ marginTop: 8 }}>
          {sharingLabel(connection)}
          <StrongValue>{connection.shared_station_count}</StrongValue>
        </Row>

        <Row>
          {T('Accounts in the contract')}
          <StrongValue>{connection.shared_account_count}</StrongValue>
        </Row>

        {/*
          O escopo por conta é uma lista, e nunca cabia na célula da tabela. Aqui
          ele tem a largura do painel.
        */}
        {/*
          Editavel enquanto o contrato vale (MV2-1816): tirar uma conta corta na
          hora o que o parceiro enxerga dela, sem suspender nem revogar. No
          revogado o servidor recusa, entao ali a linha segue sendo leitura.
        */}
        {/*
          A herança vem do contrato, e não fixada aqui: com `false` no lugar a nota
          afirmava que novas subcontas NÃO entram mesmo em contrato que herda, e a nota
          é justamente a resposta que a pessoa vem buscar (MV2-1814). Nulo — resposta
          antiga, sem o campo — conta como não herda, que é o padrão do vínculo.
        */}
        <ScopeAccountsRow
          tenantId={tenantId}
          partnerTenantId={connection.partner_tenant_id}
          inheritsNewSubaccounts={!!connection.inherits_new_subaccounts}
          editable={isEffective(connection.status)}
          onScopeChanged={onChanged}
        />
      </Card>

      <Card>
        <CardTitle>{T('Stations I receive')}</CardTitle>

        <Row style={{ marginTop: 8 }}>
          {receivingLabel(connection)}
          <StrongValue>{connection.received_station_count}</StrongValue>
        </Row>

        <Row>
          {T('Show these stations in my app')}
          <Value>
            {/*
              Switch do antd, e nao um botao proprio: ele tem `loading`, que e
              exatamente o que faltava — o toggle dispara chamada de rede e antes
              nao dava sinal nenhum de que estava em curso.
            */}
            <Switch
              checked={!!connection.received_stations_visible}
              disabled={!toggleEnabled}
              loading={busy}
              title={
                toggleEnabled
                  ? T('Show or hide these stations in my app')
                  : T('Only available on an effective contract where you receive stations')
              }
              onChange={(visible) =>
                run(
                  () => setReceivedVisibility(tenantId, connection.partner_tenant_id, visible),
                  visible ? T('Stations now visible in your app.') : T('Stations hidden in your app.'),
                )
              }
            />
          </Value>
        </Row>

        <Hint>
          {T(
            'Turning receiving on or off does not change the contract: it only hides the partner stations in your app.',
          )}
        </Hint>
      </Card>

      <Actions>
        {isEffective(connection.status) && (
          <ActionButton
            type="button"
            disabled={busy}
            onClick={() =>
              connection.status === 'SUSPENDED'
                ? run(
                    () => reactivateContract(tenantId, connection.partner_tenant_id),
                    T('Contract reactivated.'),
                  )
                : suspend()
            }
          >
            {connection.status === 'SUSPENDED' ? T('Reactivate') : T('Suspend')}
          </ActionButton>
        )}

        {connection.status !== 'REVOKED' && (
          <ActionButton type="button" danger disabled={busy} onClick={revoke}>
            {T('Revoke')}
          </ActionButton>
        )}

        {connection.status === 'REVOKED' && (
          <ActionButton type="button" danger disabled={busy} onClick={remove}>
            {T('Delete')}
          </ActionButton>
        )}
      </Actions>
    </div>
  );
};
